import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
} from "reactstrap";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import {
  hasPagePermissionWithAdmin,
  hasViewOnlyPermission,
  hasEditOrHigherPermission,
  hasApprovePermissionWithAdmin,
  isUserAdmin
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";
import {
  getSalaryRevisions,
  saveSalaryRevision,
  getEmployeesForDropdown,
  getPayrollData,
  getEmployeeSalaryComponents
} from "../../components/Api/GmsSalaryRevision";
import { searchEmployees } from "../../components/Api/GmsEmployee";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getBranches } from "../../components/Api/GmsBranch";
import apiClient from "../../components/Api/ApiClient";

// Get user session
const getUserSession = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const normalizeId = (value) => {
    if (value === null || value === undefined || value === '' || value === 'null' || value === 'undefined') {
      return null;
    }
    const numValue = typeof value === 'string' ? parseInt(value, 10) : value;
    return isNaN(numValue) || numValue <= 0 ? null : numValue;
  };

  let businessId, branchId;

  if (authUser && Object.keys(authUser).length > 0) {
    businessId = normalizeId(authUser.companyId);
    branchId = normalizeId(authUser.branchId);
  } else {
    businessId = normalizeId(user.user_Company_Id || localStorage.getItem("userCompanyId"));
    branchId = normalizeId(user.user_Branch_Id || localStorage.getItem("userBranchId"));
  }

  return {
    userId: authUser.userId || 1,
    userName: authUser.userName || "System",
    businessId: businessId,
    branchId: branchId,
    companyId: businessId,
  };
};

// Custom styles for Select components
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

const defaultFilter = {
  Revision_Employee_Id: null,
  From_EffectiveDate: null,
  To_EffectiveDate: null,
  From_ApprovedDate: null,
  To_ApprovedDate: null,
};

const defaultForm = {
  Revision_Id: 0,
  Revision_Employee_Id: null,
  Revision_EffectiveDate: new Date(),
  Revision_Employee_BS_Old: 0,
  Revision_Employee_HRA_Old: 0,
  Revision_Employee_TA_Old: 0,
  Revision_Employee_OA_Old: 0,
  Revision_Employee_GS_Old: 0,
  Employee_FoodAllowance_Old: 0,
  Employee_CommunicationAllowance_Old: 0,
  Employee_UtilityAllowance_Old: 0,
  Revision_Employee_BS: 0,
  Revision_Employee_HRA: 0,
  Revision_Employee_TA: 0,
  Revision_Employee_OA: 0,
  Revision_Employee_GS: 0,
  Employee_FoodAllowance: 0,
  Employee_CommunicationAllowance: 0,
  Employee_UtilityAllowance: 0,
  Revision_RequestDate: new Date(),
  Revision_CreatedBy: Number(localStorage.getItem("userId")) || 1,
  Revision_ApprovedDate: null,
  Revision_ApprovedBy: null,
};

export default function SalaryRevisionPage() {
  const [salaryRevisions, setSalaryRevisions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [filter, setFilter] = useState(defaultFilter);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("add"); // add, approve, view
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [payrollData, setPayrollData] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [effectiveDate, setEffectiveDate] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [employeeSearchOptions, setEmployeeSearchOptions] = useState([]);
  const [employeeSearchLoading, setEmployeeSearchLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [employeeFilterSearchOptions, setEmployeeFilterSearchOptions] = useState([]);
  const [employeeFilterSearchLoading, setEmployeeFilterSearchLoading] = useState(false);
  const [filterSearchTimeout, setFilterSearchTimeout] = useState(null);

  // Check permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsSalaryRevision');
  const isViewOnly = hasViewOnlyPermission(permissions, '/GmsSalaryRevision');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsSalaryRevision');
  const hasApprovePermission = hasApprovePermissionWithAdmin(permissions, '/GmsSalaryRevision');
  const isAdmin = isUserAdmin();
  const session = useMemo(() => getUserSession(), []);

  // Set default tab based on permissions
  useEffect(() => {
    if (isViewOnly) {
      // For view-only users, we'll handle this in the UI rendering
    }
  }, [isViewOnly]);

  useEffect(() => {
    if (hasPermission) {
      loadBusinesses();
      loadSalaryRevisions();
      loadEmployees();
      loadPayrollData();
    } else {
      window.location.href = '/Dashboard';
    }
  }, [hasPermission]);

  // Auto-populate business if user has companyId
  useEffect(() => {
    if (session.businessId && businesses.length > 0 && !selectedBusiness) {
      const business = businesses.find(b => b.value === session.businessId);
      if (business) {
        setSelectedBusiness(business);
      }
    }
  }, [businesses, session.businessId]);

  // Auto-populate branch if user has branchId
  useEffect(() => {
    if (session.branchId && branches.length > 0 && selectedBusiness?.value === session.businessId && !selectedBranch) {
      const branch = branches.find(b => b.value === session.branchId);
      if (branch) {
        setSelectedBranch(branch);
      }
    }
  }, [branches, session.branchId, selectedBusiness]);

  // Load branches when business changes
  useEffect(() => {
    if (selectedBusiness) {
      loadBranches(selectedBusiness.value);
    } else {
      setBranches([]);
      setSelectedBranch(null);
    }
  }, [selectedBusiness]);

  // Reload employees when business/branch changes
  useEffect(() => {
    if (businesses.length > 0) {
      loadEmployees();
    }
  }, [selectedBusiness, selectedBranch]);

  const loadSalaryRevisions = async () => {
    setLoading(true);
    try {
      const data = await getSalaryRevisions(filter);

      // Fetch employee details for each revision
      const enrichedData = await Promise.all(
        data.map(async (revision) => {
          try {
            // Get employee details
            const employeeResponse = await apiClient.post(`/api/Employee/get`, {
              employee_Id: revision.revision_Employee_Id
            });
            const employee = employeeResponse.data && employeeResponse.data.length > 0 ? employeeResponse.data[0] : null;

            // Get business details
            let business = null;
            if (employee && employee.employee_Business_Id) {
              const businessResponse = await apiClient.post(`/api/Business/get`, {
                business_Id: employee.employee_Business_Id
              });
              business = businessResponse.data && businessResponse.data.length > 0 ? businessResponse.data[0] : null;
            }

            // Get designation details
            let designation = null;
            if (employee && employee.employee_Designation_ID) {
              const designationResponse = await apiClient.post(`/api/Designation/get`, {
                designation_Id: employee.employee_Designation_ID
              });
              designation = designationResponse.data && designationResponse.data.length > 0 ? designationResponse.data[0] : null;
            }

            // Map API response property names to component expected names
            return {
              Revision_Id: revision.revision_Id,
              Revision_Employee_Id: revision.revision_Employee_Id,
              Revision_EffectiveDate: revision.revision_EffectiveDate,
              Revision_Employee_BS_Old: revision.revision_Employee_BS_Old,
              Revision_Employee_HRA_Old: revision.revision_Employee_HRA_Old,
              Revision_Employee_TA_Old: revision.revision_Employee_TA_Old,
              Revision_Employee_OA_Old: revision.revision_Employee_OA_Old,
              Revision_Employee_GS_Old: revision.revision_Employee_GS_Old,
              Revision_Employee_BS: revision.revision_Employee_BS,
              Revision_Employee_HRA: revision.revision_Employee_HRA,
              Revision_Employee_TA: revision.revision_Employee_TA,
              Revision_Employee_OA: revision.revision_Employee_OA,
              Revision_Employee_GS: revision.revision_Employee_GS,
              Revision_RequestDate: revision.revision_RequestDate,
              Revision_CreatedBy: revision.revision_CreatedBy,
              Revision_ApprovedDate: revision.revision_ApprovedDate,
              Revision_ApprovedBy: revision.revision_ApprovedBy,
              Employee_FoodAllowance_Old: revision.employee_FoodAllowance_Old,
              Employee_CommunicationAllowance_Old: revision.employee_CommunicationAllowance_Old,
              Employee_UtilityAllowance_Old: revision.employee_UtilityAllowance_Old,
              Employee_FoodAllowance: revision.employee_FoodAllowance,
              Employee_CommunicationAllowance: revision.employee_CommunicationAllowance,
              Employee_UtilityAllowance: revision.employee_UtilityAllowance,
              // Add employee details
              employee_Code: employee ? employee.employee_Code : "N/A",
              employee_NameEn: employee ? employee.employee_NameEn : "N/A",
              business_NameEn: business ? business.business_NameEn : "N/A",
              designation_NameEn: designation ? designation.designation_NameEn : "N/A"
            };
          } catch (error) {
            console.error(`Error fetching details for revision ${revision.revision_Id}:`, error);
            // Return revision with N/A values if employee details can't be fetched
            return {
              Revision_Id: revision.revision_Id,
              Revision_Employee_Id: revision.revision_Employee_Id,
              Revision_EffectiveDate: revision.revision_EffectiveDate,
              Revision_Employee_BS_Old: revision.revision_Employee_BS_Old,
              Revision_Employee_HRA_Old: revision.revision_Employee_HRA_Old,
              Revision_Employee_TA_Old: revision.revision_Employee_TA_Old,
              Revision_Employee_OA_Old: revision.revision_Employee_OA_Old,
              Revision_Employee_GS_Old: revision.revision_Employee_GS_Old,
              Revision_Employee_BS: revision.revision_Employee_BS,
              Revision_Employee_HRA: revision.revision_Employee_HRA,
              Revision_Employee_TA: revision.revision_Employee_TA,
              Revision_Employee_OA: revision.revision_Employee_OA,
              Revision_Employee_GS: revision.revision_Employee_GS,
              Revision_RequestDate: revision.revision_RequestDate,
              Revision_CreatedBy: revision.revision_CreatedBy,
              Revision_ApprovedDate: revision.revision_ApprovedDate,
              Revision_ApprovedBy: revision.revision_ApprovedBy,
              Employee_FoodAllowance_Old: revision.employee_FoodAllowance_Old,
              Employee_CommunicationAllowance_Old: revision.employee_CommunicationAllowance_Old,
              Employee_UtilityAllowance_Old: revision.employee_UtilityAllowance_Old,
              Employee_FoodAllowance: revision.employee_FoodAllowance,
              Employee_CommunicationAllowance: revision.employee_CommunicationAllowance,
              Employee_UtilityAllowance: revision.employee_UtilityAllowance,
              employee_Code: "N/A",
              employee_NameEn: "N/A",
              business_NameEn: "N/A",
              designation_NameEn: "N/A"
            };
          }
        })
      );

      setSalaryRevisions(enrichedData);
      setTotalCount(enrichedData.length);
    } catch (error) {
      console.error("Error loading salary revisions:", error);
      Swal.fire("Error", "Failed to load salary revisions", "error");
    } finally {
      setLoading(false);
    }
  };

  const loadBusinesses = async () => {
    try {
      const data = await getBusinesses({ business_Active: true });
      const businessOptions = data.map(b => ({
        value: b.business_Id,
        label: b.business_NameEn || b.business_NameAr
      }));
      setBusinesses(businessOptions);
    } catch (error) {
      console.error("Error loading businesses:", error);
    }
  };

  const loadBranches = async (businessId) => {
    try {
      const data = await getBranches({ Branch_Buisness_Id: businessId, branch_Active: true });
      const branchOptions = data.map(b => ({
        value: b.branch_Id || b.Branch_Id,
        label: b.branch_NameEn || b.Branch_NameEn || b.branch_Name || b.Branch_Name
      }));
      setBranches(branchOptions);
    } catch (error) {
      console.error("Error loading branches:", error);
      setBranches([]);
    }
  };

  const loadEmployees = async () => {
    try {
      const filter = {};
      if (selectedBusiness) {
        filter.employee_Business_Id = selectedBusiness.value;
      }
      if (selectedBranch) {
        filter.employee_Branch_Id = selectedBranch.value;
      }
      // If user has session business/branch, use those if no selection
      if (!selectedBusiness && session.businessId) {
        filter.employee_Business_Id = session.businessId;
      }
      if (!selectedBranch && session.branchId && session.businessId) {
        filter.employee_Branch_Id = session.branchId;
      }

      const data = await getEmployeesForDropdown(filter);
      setEmployees(data);
    } catch (error) {
      console.error("Error loading employees:", error);
    }
  };

  // Manual search function for employee search
  const searchEmployeesManually = async (inputValue) => {
    if (!inputValue || inputValue.length < 3) {
      setEmployeeSearchOptions([]);
      return;
    }

    try {
      setEmployeeSearchLoading(true);
      const businessId = selectedBusiness?.value || session.businessId || null;
      const branchId = selectedBranch?.value || (session.branchId && session.businessId ? session.branchId : null);
      const response = await searchEmployees(inputValue, 1, 20, businessId, branchId);

      const formattedOptions = response.items.map(employee => ({
        value: employee.employee_Id,
        label: `${employee.employee_NameEn || 'N/A'} — ${employee.emp_No || employee.Emp_No || 'N/A'}`,
        employee: employee
      }));

      setEmployeeSearchOptions(formattedOptions);
    } catch (error) {
      console.error('Error searching employees:', error);
      setEmployeeSearchOptions([]);
    } finally {
      setEmployeeSearchLoading(false);
    }
  };

  // Manual search function for employee filter search
  const searchEmployeesFilterManually = async (inputValue) => {
    if (!inputValue || inputValue.length < 3) {
      setEmployeeFilterSearchOptions([]);
      return;
    }

    try {
      setEmployeeFilterSearchLoading(true);
      const businessId = selectedBusiness?.value || session.businessId || null;
      const branchId = selectedBranch?.value || (session.branchId && session.businessId ? session.branchId : null);
      const response = await searchEmployees(inputValue, 1, 20, businessId, branchId);

      const formattedOptions = response.items.map(employee => ({
        value: employee.employee_Id,
        label: `${employee.employee_NameEn || 'N/A'} — ${employee.emp_No || employee.Emp_No || 'N/A'}`,
        employee: employee
      }));

      setEmployeeFilterSearchOptions(formattedOptions);
    } catch (error) {
      console.error('Error searching employees:', error);
      setEmployeeFilterSearchOptions([]);
    } finally {
      setEmployeeFilterSearchLoading(false);
    }
  };

  const loadPayrollData = async () => {
    try {
      const data = await getPayrollData();
      setPayrollData(data);
      // Find the payroll with payroll_IsPosted: false
      const activePayroll = data.find(p => !p.payroll_IsPosted);
      if (activePayroll) {
        setEffectiveDate(new Date(activePayroll.payroll_Date));
      }
    } catch (error) {
      console.error("Error loading payroll data:", error);
    }
  };

  const handleFilterChange = (name, value) => {
    setFilter(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFormChange = (name, value) => {
    setForm(prev => {
      const updated = {
        ...prev,
        [name]: value
      };

      // Auto-calculate gross salary when components change
      if (name.includes('Employee_BS') || name.includes('Employee_HRA') ||
        name.includes('Employee_TA') || name.includes('Employee_OA') ||
        name.includes('FoodAllowance') || name.includes('CommunicationAllowance') ||
        name.includes('UtilityAllowance')) {
        if (!name.includes('_Old')) {
          updated.Revision_Employee_GS =
            (updated.Revision_Employee_BS || 0) +
            (updated.Revision_Employee_HRA || 0) +
            (updated.Revision_Employee_TA || 0) +
            (updated.Revision_Employee_OA || 0) +
            (updated.Employee_FoodAllowance || 0) +
            (updated.Employee_CommunicationAllowance || 0) +
            (updated.Employee_UtilityAllowance || 0);
        }
      }

      return updated;
    });
  };

  const handleEmployeeSelect = async (selectedOption) => {
    if (selectedOption && modalMode !== "view") {
      setSelectedEmployee(selectedOption);
      handleFormChange("Revision_Employee_Id", selectedOption.value);

      try {
        const salaryComponents = await getEmployeeSalaryComponents(selectedOption.value);
        if (salaryComponents) {
          // Set old values (read-only)
          handleFormChange("Revision_Employee_BS_Old", salaryComponents.basicSalary);
          handleFormChange("Revision_Employee_HRA_Old", salaryComponents.hra);
          handleFormChange("Revision_Employee_TA_Old", salaryComponents.transportAllowance);
          handleFormChange("Revision_Employee_OA_Old", salaryComponents.otherAllowance);
          handleFormChange("Revision_Employee_GS_Old", salaryComponents.grossSalary);
          handleFormChange("Employee_FoodAllowance_Old", salaryComponents.foodAllowance);
          handleFormChange("Employee_CommunicationAllowance_Old", salaryComponents.communicationAllowance);
          handleFormChange("Employee_UtilityAllowance_Old", salaryComponents.utilityAllowance);

          // Set new values (editable) - initially same as old values
          handleFormChange("Revision_Employee_BS", salaryComponents.basicSalary);
          handleFormChange("Revision_Employee_HRA", salaryComponents.hra);
          handleFormChange("Revision_Employee_TA", salaryComponents.transportAllowance);
          handleFormChange("Revision_Employee_OA", salaryComponents.otherAllowance);
          handleFormChange("Employee_FoodAllowance", salaryComponents.foodAllowance);
          handleFormChange("Employee_CommunicationAllowance", salaryComponents.communicationAllowance);
          handleFormChange("Employee_UtilityAllowance", salaryComponents.utilityAllowance);
          handleFormChange("Revision_Employee_GS", salaryComponents.grossSalary);
        }
      } catch (error) {
        console.error("Error loading employee salary components:", error);
        Swal.fire("Error", "Failed to load employee salary details", "error");
      }
    }
  };

  const applyFilter = () => {
    loadSalaryRevisions();
  };

  const resetFilter = () => {
    setFilter(defaultFilter);
    setPage(1);
  };

  const openAddModal = () => {
    setForm({
      ...defaultForm,
      Revision_EffectiveDate: effectiveDate || new Date(),
      Revision_RequestDate: new Date(),
    });
    setSelectedEmployee(null);
    setModalMode("add");
    setShowModal(true);
  };

  const openApproveModal = (revision) => {
    setForm({
      ...revision,
      Revision_ApprovedBy: getUserSession().userId,
      Revision_ApprovedDate: new Date(),
    });
    const employee = employees.find(emp => emp.value === revision.Revision_Employee_Id);
    setSelectedEmployee(employee);
    setModalMode("approve");
    setShowModal(true);
  };

  const openViewModal = (revision) => {
    setForm(revision);
    const employee = employees.find(emp => emp.value === revision.Revision_Employee_Id);
    setSelectedEmployee(employee);
    setModalMode("view");
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaveLoading(true);

    try {
      const payload = {
        ...form,
        Revision_EffectiveDate: effectiveDate || form.Revision_EffectiveDate,
      };

      const result = await saveSalaryRevision(payload);

      if (result.success) {
        Swal.fire("Success", result.message, "success");
        setShowModal(false);
        loadSalaryRevisions();
      }
    } catch (error) {
      console.error("Error saving salary revision:", error);
      Swal.fire("Error", error.message || "Failed to save salary revision", "error");
    } finally {
      setSaveLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return "N/A";
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const paginatedData = salaryRevisions.slice((page - 1) * pageSize, page * pageSize);
  const totalPages = Math.ceil(totalCount / pageSize);

  const isFieldReadOnly = (fieldName) => {
    return modalMode === "view" ||
      (modalMode === "approve" && fieldName === "employee") ||
      fieldName.includes("_Old");
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb title="Salary Revision" breadcrumbItem="Employee Management" />

        <Row>
          <Col xs={12}>
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div>
                    {hasEditOrHigher && (
                      <Button color="primary" onClick={openAddModal}>
                        <i className="bx bx-plus"></i> Add Salary Revision
                      </Button>
                    )}
                  </div>
                  <Button
                    color="primary"
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    {showFilters ? "Hide Filters" : "Show Filters"}
                  </Button>
                </div>

                {/* Filters */}
                {showFilters && (
                  <Card className="mb-4">
                    <CardBody>
                      <h5 className="card-title mb-3">Filters</h5>
                      <Row>
                        <Col md={6} lg={3}>
                          <FormGroup>
                            <Label>Company</Label>
                            <Select
                              value={selectedBusiness}
                              onChange={(option) => {
                                setSelectedBusiness(option);
                                setSelectedBranch(null);
                                setEmployeeSearchOptions([]);
                                setEmployeeFilterSearchOptions([]);
                                handleFilterChange("Revision_Employee_Id", null);
                              }}
                              options={businesses}
                              styles={selectStyles}
                              isClearable
                              isSearchable
                              placeholder="Select Company"
                              className="react-select-container"
                              classNamePrefix="react-select"
                              isDisabled={!!session.businessId}
                            />
                          </FormGroup>
                        </Col>
                        <Col md={6} lg={3}>
                          <FormGroup>
                            <Label>Branch</Label>
                            <Select
                              value={selectedBranch}
                              onChange={(option) => {
                                setSelectedBranch(option);
                                setEmployeeSearchOptions([]);
                                setEmployeeFilterSearchOptions([]);
                                handleFilterChange("Revision_Employee_Id", null);
                              }}
                              options={branches}
                              styles={selectStyles}
                              isClearable
                              isSearchable
                              placeholder="Select Branch"
                              className="react-select-container"
                              classNamePrefix="react-select"
                              isDisabled={!!session.branchId || !selectedBusiness}
                            />
                          </FormGroup>
                        </Col>
                        <Col md={6} lg={3}>
                          <FormGroup>
                            <Label>Employee</Label>
                            <Select
                              value={filter.Revision_Employee_Id ? employeeFilterSearchOptions.find(emp => emp.value === filter.Revision_Employee_Id) : null}
                              onChange={(selectedOption) => {
                                handleFilterChange("Revision_Employee_Id", selectedOption?.value || null);
                                if (selectedOption) {
                                  setEmployeeFilterSearchOptions([]);
                                }
                              }}
                              options={employeeFilterSearchOptions}
                              isLoading={employeeFilterSearchLoading}
                              filterOption={() => true}
                              noOptionsMessage={({ inputValue }) => {
                                if (employeeFilterSearchLoading) return "Searching...";
                                if (!inputValue || inputValue.length < 3) return "Type at least 3 characters to search";
                                return "No employees found";
                              }}
                              loadingMessage={() => "Searching employees..."}
                              onInputChange={(inputValue, { action }) => {
                                if (action === 'input-change') {
                                  if (filterSearchTimeout) {
                                    clearTimeout(filterSearchTimeout);
                                  }
                                  const timeout = setTimeout(() => {
                                    searchEmployeesFilterManually(inputValue);
                                  }, 300);
                                  setFilterSearchTimeout(timeout);
                                }
                                return inputValue;
                              }}
                              styles={selectStyles}
                              isClearable
                              placeholder="Type at least 3 characters to search..."
                              className="react-select-container"
                              classNamePrefix="react-select"
                            />
                          </FormGroup>
                        </Col>
                        <Col md={6} lg={3}>
                          <FormGroup>
                            <Label>From Effective Date</Label>
                            <DatePicker
                              selected={filter.From_EffectiveDate}
                              onChange={(date) => handleFilterChange("From_EffectiveDate", date)}
                              className="form-control"
                              dateFormat="dd/MM/yyyy"
                              placeholderText="Select Date"
                              isClearable
                            />
                          </FormGroup>
                        </Col>
                        <Col md={6} lg={3}>
                          <FormGroup>
                            <Label>To Effective Date</Label>
                            <DatePicker
                              selected={filter.To_EffectiveDate}
                              onChange={(date) => handleFilterChange("To_EffectiveDate", date)}
                              className="form-control"
                              dateFormat="dd/MM/yyyy"
                              placeholderText="Select Date"
                              isClearable
                            />
                          </FormGroup>
                        </Col>
                        <Col md={6} lg={3}>
                          <FormGroup>
                            <Label>From Approved Date</Label>
                            <DatePicker
                              selected={filter.From_ApprovedDate}
                              onChange={(date) => handleFilterChange("From_ApprovedDate", date)}
                              className="form-control"
                              dateFormat="dd/MM/yyyy"
                              placeholderText="Select Date"
                              isClearable
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                      <Row>
                        <Col md={6} lg={3}>
                          <FormGroup>
                            <Label>To Approved Date</Label>
                            <DatePicker
                              selected={filter.To_ApprovedDate}
                              onChange={(date) => handleFilterChange("To_ApprovedDate", date)}
                              className="form-control"
                              dateFormat="dd/MM/yyyy"
                              placeholderText="Select Date"
                              isClearable
                            />
                          </FormGroup>
                        </Col>
                        <Col md={6} lg={3} className="d-flex align-items-end">
                          <FormGroup className="w-100">
                            <Button color="primary" onClick={applyFilter} className="me-2">
                              Apply
                            </Button>
                            <Button color="secondary" onClick={resetFilter}>
                              Reset
                            </Button>
                          </FormGroup>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                )}

                {/* Data Table */}
                <div className="table-responsive">
                  {loading ? (
                    <div className="text-center py-4">
                      <ClipLoader color="#556ee6" size={35} />
                    </div>
                  ) : (
                    <Table className="table table-bordered table-striped mb-0">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Employee Code</th>
                          <th>Name</th>
                          <th>Company Name</th>
                          <th>Designation</th>
                          <th>Old Gross Salary</th>
                          <th>New Gross Salary</th>
                          <th>Effective Date</th>
                          <th>Request Date</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedData.length > 0 ? (
                          paginatedData.map((revision, index) => (
                            <tr key={revision.Revision_Id}>
                              <td>{(page - 1) * pageSize + index + 1}</td>
                              <td>{revision.employee_Code || "N/A"}</td>
                              <td>{revision.employee_NameEn || "N/A"}</td>
                              <td>{revision.business_NameEn || "N/A"}</td>
                              <td>{revision.designation_NameEn || "N/A"}</td>
                              <td>{formatCurrency(revision.Revision_Employee_GS_Old)}</td>
                              <td>{formatCurrency(revision.Revision_Employee_GS)}</td>
                              <td>{formatDate(revision.Revision_EffectiveDate)}</td>
                              <td>{formatDate(revision.Revision_RequestDate)}</td>
                              <td>
                                {revision.Revision_ApprovedBy && revision.Revision_ApprovedDate ? (
                                  <Button
                                    color="info"
                                    size="sm"
                                    onClick={() => openViewModal(revision)}
                                  >
                                    <i className="bx bx-show"></i> View
                                  </Button>
                                ) : (
                                  hasApprovePermission && (
                                    <Button
                                      color="success"
                                      size="sm"
                                      onClick={() => openApproveModal(revision)}
                                    >
                                      <i className="bx bx-check"></i> Approve
                                    </Button>
                                  )
                                )}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="10" className="text-center">
                              No salary revisions found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  )}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <div>
                      Showing {Math.min((page - 1) * pageSize + 1, totalCount)} to {Math.min(page * pageSize, totalCount)} of {totalCount} entries
                    </div>
                    <div className="d-flex">
                      <Button
                        color="light"
                        size="sm"
                        disabled={page === 1}
                        onClick={() => setPage(page - 1)}
                        className="me-1"
                      >
                        Previous
                      </Button>
                      {[...Array(totalPages)].map((_, i) => (
                        <Button
                          key={i}
                          color={page === i + 1 ? "primary" : "light"}
                          size="sm"
                          onClick={() => setPage(i + 1)}
                          className="me-1"
                        >
                          {i + 1}
                        </Button>
                      ))}
                      <Button
                        color="light"
                        size="sm"
                        disabled={page === totalPages}
                        onClick={() => setPage(page + 1)}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Modal for Add/Edit/View */}
        <Modal isOpen={showModal} toggle={() => setShowModal(false)} size="xl">
          <ModalHeader toggle={() => setShowModal(false)}>
            {modalMode === "add" ? "Add Salary Revision" :
              modalMode === "approve" ? "Approve Salary Revision" :
                "View Salary Revision"}
          </ModalHeader>
          <Form onSubmit={handleSubmit}>
            <ModalBody>
              <Row>
                <Col md={3}>
                  <FormGroup>
                    <Label>Company *</Label>
                    <Select
                      value={selectedBusiness}
                      onChange={(option) => {
                        setSelectedBusiness(option);
                        setSelectedBranch(null);
                        setSelectedEmployee(null);
                        handleFormChange("Revision_Employee_Id", null);
                      }}
                      options={businesses}
                      styles={selectStyles}
                      isClearable
                      isSearchable
                      placeholder="Select Company"
                      isDisabled={modalMode !== "add" || !!session.businessId}
                      className="react-select-container"
                      classNamePrefix="react-select"
                    />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label>Branch</Label>
                    <Select
                      value={selectedBranch}
                      onChange={(option) => {
                        setSelectedBranch(option);
                        setSelectedEmployee(null);
                        handleFormChange("Revision_Employee_Id", null);
                      }}
                      options={branches}
                      styles={selectStyles}
                      isClearable
                      isSearchable
                      placeholder="Select Branch"
                      isDisabled={modalMode !== "add" || !!session.branchId || !selectedBusiness}
                      className="react-select-container"
                      classNamePrefix="react-select"
                    />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label>Employee *</Label>
                    <Select
                      value={selectedEmployee}
                      onChange={(option) => {
                        handleEmployeeSelect(option);
                        if (option) {
                          setEmployeeSearchOptions([]);
                        }
                      }}
                      options={employeeSearchOptions}
                      isLoading={employeeSearchLoading}
                      filterOption={() => true}
                      noOptionsMessage={({ inputValue }) => {
                        if (employeeSearchLoading) return "Searching...";
                        if (!inputValue || inputValue.length < 3) return "Type at least 3 characters to search";
                        return "No employees found";
                      }}
                      loadingMessage={() => "Searching employees..."}
                      onInputChange={(inputValue, { action }) => {
                        if (action === 'input-change' && !isFieldReadOnly("employee")) {
                          if (searchTimeout) {
                            clearTimeout(searchTimeout);
                          }
                          const timeout = setTimeout(() => {
                            searchEmployeesManually(inputValue);
                          }, 300);
                          setSearchTimeout(timeout);
                        }
                        return inputValue;
                      }}
                      styles={selectStyles}
                      placeholder="Type at least 3 characters to search..."
                      isDisabled={isFieldReadOnly("employee")}
                      className="react-select-container"
                      classNamePrefix="react-select"
                    />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label>Request Date *</Label>
                    <DatePicker
                      selected={form.Revision_RequestDate ? new Date(form.Revision_RequestDate) : new Date()}
                      onChange={(date) => handleFormChange("Revision_RequestDate", date)}
                      className="form-control"
                      dateFormat="dd/MM/yyyy"
                      disabled={isFieldReadOnly("requestDate")}
                    />
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label>Effective Date *</Label>
                    <DatePicker
                      selected={effectiveDate}
                      className="form-control"
                      dateFormat="dd/MM/yyyy"
                      disabled={true}
                    />
                    <small className="text-muted">Based on current payroll period</small>
                  </FormGroup>
                </Col>
              </Row>

              <hr />
              <h5>Salary Components</h5>

              <Row>
                <Col md={6}>
                  <h6 className="text-muted">Current Salary</h6>
                  <FormGroup>
                    <Label>Old Basic Salary</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={form.Revision_Employee_BS_Old}
                      readOnly
                      className="bg-light"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Old House Rent Allowance</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={form.Revision_Employee_HRA_Old}
                      readOnly
                      className="bg-light"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Old Transport Allowance</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={form.Revision_Employee_TA_Old}
                      readOnly
                      className="bg-light"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Old Other Allowance</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={form.Revision_Employee_OA_Old}
                      readOnly
                      className="bg-light"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Old Food Allowance</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={form.Employee_FoodAllowance_Old}
                      readOnly
                      className="bg-light"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Old Communication Allowance</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={form.Employee_CommunicationAllowance_Old}
                      readOnly
                      className="bg-light"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Old Utility Allowance</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={form.Employee_UtilityAllowance_Old}
                      readOnly
                      className="bg-light"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Old Gross Salary</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={form.Revision_Employee_GS_Old}
                      readOnly
                      className="bg-light"
                    />
                  </FormGroup>
                </Col>

                <Col md={6}>
                  <h6 className="text-muted">Revised Salary</h6>
                  <FormGroup>
                    <Label>New Basic Salary *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={form.Revision_Employee_BS}
                      onChange={(e) => handleFormChange("Revision_Employee_BS", parseFloat(e.target.value) || 0)}
                      disabled={isFieldReadOnly("newSalary")}
                      required
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>New House Rent Allowance *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={form.Revision_Employee_HRA}
                      onChange={(e) => handleFormChange("Revision_Employee_HRA", parseFloat(e.target.value) || 0)}
                      disabled={isFieldReadOnly("newSalary")}
                      required
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>New Transport Allowance *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={form.Revision_Employee_TA}
                      onChange={(e) => handleFormChange("Revision_Employee_TA", parseFloat(e.target.value) || 0)}
                      disabled={isFieldReadOnly("newSalary")}
                      required
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>New Other Allowance *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={form.Revision_Employee_OA}
                      onChange={(e) => handleFormChange("Revision_Employee_OA", parseFloat(e.target.value) || 0)}
                      disabled={isFieldReadOnly("newSalary")}
                      required
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>New Food Allowance *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={form.Employee_FoodAllowance}
                      onChange={(e) => handleFormChange("Employee_FoodAllowance", parseFloat(e.target.value) || 0)}
                      disabled={isFieldReadOnly("newSalary")}
                      required
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>New Communication Allowance *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={form.Employee_CommunicationAllowance}
                      onChange={(e) => handleFormChange("Employee_CommunicationAllowance", parseFloat(e.target.value) || 0)}
                      disabled={isFieldReadOnly("newSalary")}
                      required
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>New Utility Allowance *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={form.Employee_UtilityAllowance}
                      onChange={(e) => handleFormChange("Employee_UtilityAllowance", parseFloat(e.target.value) || 0)}
                      disabled={isFieldReadOnly("newSalary")}
                      required
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>New Gross Salary</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={form.Revision_Employee_GS}
                      readOnly
                      className="bg-light"
                    />
                    <small className="text-muted">Auto-calculated based on components</small>
                  </FormGroup>
                </Col>
              </Row>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              {modalMode !== "view" && (
                <Button color="primary" type="submit" disabled={saveLoading}>
                  {saveLoading ? <ClipLoader color="white" size={20} /> :
                    modalMode === "approve" ? "Approve" : "Save"}
                </Button>
              )}
            </ModalFooter>
          </Form>
        </Modal>
      </Container>
    </div>
  );
} 